import React, { useCallback } from 'react';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';

type Params = {
  hasUnsavedChangesRef: React.MutableRefObject<boolean>;
  isDirty: boolean;
  onClose: () => void;
  setValue: ReturnType<typeof useFormContext>['setValue'];
  clearErrors: ReturnType<typeof useFormContext>['clearErrors'];
  setActiveTab: (tab: 'current' | 'history') => void;
  userPromptMode: string;
};

export const useExitWithoutSaving = ({
  hasUnsavedChangesRef,
  isDirty,
  onClose,
  setValue,
  clearErrors,
  setActiveTab,
  userPromptMode,
}: Params) => {
  const [
    isExitWithoutSavingModalOpen,
    openExitWithoutSavingModal,
    closeExitWithoutSavingModal,
  ] = useModal();

  // Latch: once the form becomes dirty, mark unsaved changes. The ref is
  // also set directly by applyHistoryEntryToCurrent for history applies.
  if (isDirty) {
    hasUnsavedChangesRef.current = true;
  }

  const closeAndReset = useCallback(() => {
    closeExitWithoutSavingModal();
    if (userPromptMode !== 'initial') {
      setValue('userPromptMode', 'view');
    }
    clearErrors('root.parseSkills');
    clearErrors('root.suggestedSkills');
    setActiveTab('current');
    onClose();
  }, [
    clearErrors,
    closeExitWithoutSavingModal,
    onClose,
    setActiveTab,
    setValue,
    userPromptMode,
  ]);

  const handleOnClose = useCallback(() => {
    if (hasUnsavedChangesRef.current) {
      openExitWithoutSavingModal();
      return;
    }
    closeAndReset();
  }, [closeAndReset, hasUnsavedChangesRef, openExitWithoutSavingModal]);

  return {
    isExitWithoutSavingModalOpen,
    handleOnClose,
    closeExitWithoutSavingModal,
    closeAndReset,
  };
};
